// ── EVIDENCE — what VeriGRC may claim about an artifact's integrity ────────────────────────────
//
// ⚠ DECLARED ONCE, AND IN SHARED FOR A REASON. This sentence is the honesty boundary of the whole
//   evidence model, and it now has TWO consumers — the connector that captures (apps/worker,
//   packages/connectors) and the surface that shows it (apps/customer-app). A second copy would
//   drift the moment someone "tightened" one of them, and the drift would be invisible: both would
//   keep rendering, disagreeing only about what VeriGRC promises.
//
// ⚠ WHY THE CLAIM IS THIS NARROW. A repository's integrity signal is whatever hash that provider
//   happens to return, and none of them is cryptographic proof. SharePoint returns `quickXorHash`
//   (the only hash it guarantees — `sha256Hash` is documented "isn't supported. Don't use."), a
//   PROPRIETARY, NON-CRYPTOGRAPHIC checksum. Box returns SHA-1, which is collision-broken for
//   adversarial use. Both detect that a file changed; neither proves that one did not. On a
//   compliance surface those are different promises, and only the first is ever ours.
//
// ⚠ AND THE CLAIM NAMES THE ALGORITHM THE ARTIFACT WAS ACTUALLY FINGERPRINTED WITH — never a
//   hardcoded one. These strings were SharePoint-bound: a Box artifact would have rendered
//   "Microsoft quickXorHash" over a SHA-1 hash, which is a false provider claim in a
//   compliance-evidence product. ⚠ It fails SILENTLY in the UI, where the database's 23514 would
//   at least have been loud. Parameterised so the wrong claim is unrepresentable rather than merely
//   avoided.

/** The algorithms this build actually WRITES. ⓘ 'sha256' is reserved in the schema and unwritten. */
export type CaptureHashAlgorithm = 'quickXorHash' | 'sha1';

/** SharePoint's. The only hash Graph guarantees on a drive item. */
export const SHAREPOINT_HASH_ALGORITHM = 'quickXorHash' as const;

/** Box's. Returned on every file alongside `file_version.id`. */
export const BOX_HASH_ALGORITHM = 'sha1' as const;

/**
 * How each algorithm is named to a tenant, and what may be said about it.
 *
 * ⚠ NO ENTRY MAY CARRY A SECURITY WORD. SHA-1 is adequate for "did this file change" and is
 *   collision-broken for adversarial use; quickXorHash is not cryptographic at all. Naming the
 *   algorithm is honest, and the disclaimer tail carries the rest — see OVER_CLAIMING_TERMS, which
 *   a test asserts the ABSENCE of across every surface.
 */
const ALGORITHM_PHRASE: Record<string, { long: string; short: string }> = {
  quickXorHash: {
    long: 'Microsoft quickXorHash, a proprietary non-cryptographic checksum',
    short: 'quickXorHash, non-cryptographic',
  },
  sha1: {
    long: 'Box SHA-1 content hash',
    short: 'SHA-1 content hash',
  },
};

/**
 * ⚠ AN UNRECOGNISED ALGORITHM IS NAMED, NOT GUESSED — the same discipline the provider registry
 *   uses for an unknown connector. Falling back to quickXorHash's wording would put a confident
 *   WRONG algorithm in front of a tenant, which is the exact defect this parameterisation exists to
 *   remove; falling back to the version-only claim would assert "no content hash available" over a
 *   file that has one. Echoing the stored value is unlovely and true.
 */
const unknownPhrase = (algorithm: string) => ({
  long: `an unrecognised checksum reported as "${algorithm}"`,
  short: `unrecognised checksum "${algorithm}"`,
});

/**
 * The full claim for the algorithm this artifact was ACTUALLY fingerprinted with.
 *
 * ⚠ Surfaces pass the STORED `hash_algorithm`, never a constant — otherwise the parameterisation
 *   buys nothing and the claim is hardcoded again one layer up.
 */
export function integrityClaimFor(algorithm: string): string {
  const phrase = ALGORITHM_PHRASE[algorithm] ?? unknownPhrase(algorithm);
  return `Change-detection against a recorded baseline (${phrase.long}) `
    + 'plus version lineage. This is not cryptographic proof that a file is unmodified.';
}

/** The short form for a dense row. Same promise, fewer words — never a tick, never "verified". */
export function integrityShortFor(algorithm: string): string {
  const phrase = ALGORITHM_PHRASE[algorithm] ?? unknownPhrase(algorithm);
  return `Change-detection (${phrase.short})`;
}

/**
 * ⚠ SHAREPOINT'S WORDING, DERIVED — NOT A SECOND COPY. These are the exact strings the shipped
 *   SharePoint surface has always rendered, and they must stay BYTE-IDENTICAL; deriving them from
 *   the same function that serves Box is what makes a drift in the shared wording show up as a
 *   failing SharePoint regression rather than as a quietly reworded compliance claim.
 */
export const INTEGRITY_CLAIM = integrityClaimFor(SHAREPOINT_HASH_ALGORITHM);

/** @see INTEGRITY_CLAIM — same derivation, same byte-identical guarantee. */
export const INTEGRITY_SHORT = integrityShortFor(SHAREPOINT_HASH_ALGORITHM);

/**
 * ⚠ THE WEAKER CASE, SAID IN ITS OWN WORDS. Some files carry no hash at all — Microsoft documents
 * every hash as "(if available)" — and for those, change is detected only when the provider records
 * a NEW VERSION. Showing the quickXorHash claim over such a file would describe a comparison that
 * never happened, which is the quiet over-claim this whole vocabulary exists to prevent.
 */
export const INTEGRITY_CLAIM_VERSION_ONLY =
  'Change-detection by version lineage only: no content hash is available for this file, so a change '
  + 'is detected when the repository records a new version. This is weaker than comparing a checksum, '
  + 'and it is not cryptographic proof that a file is unmodified.';

/** The short form of the weaker case. */
export const INTEGRITY_SHORT_VERSION_ONLY = 'Version-tracked only — no content hash';


/**
 * ⚠ THE WORDS THAT WOULD OVER-CLAIM, kept as a list so a test can assert their ABSENCE as a class
 *   rather than chasing one phrasing at a time. Any surface describing evidence integrity is checked
 *   against this.
 */
export const OVER_CLAIMING_TERMS = [
  'tamper-proof', 'tamper proof', 'tamperproof', 'tamper-evident',
  'cryptographically sealed', 'cryptographic proof', 'cryptographically verified',
  'immutable', 'WORM', 'notarised', 'notarized',
] as const;
